import React, { useEffect, useRef, useState } from 'react'
import classes from './index.module.css'
import Loader from '../../../Components/GeneralComponents/Loader'
import { Wrapper } from '../../../Components/page/Wrapper'
import { Header } from '../../../Components/page/Header'
import { HeaderInfo } from '../../../Components/page/HeaderInfo'
import { Actions } from '../../../Components/page/Actions'
import { ButtonOutline } from '../../../Components/GeneralComponents/ButtonOutline/ButtonOutline'
import { Navigation } from '../../../Components/page/Navigation'

import PageTitle from '../components/PageTitle'
import { useParams } from 'react-router-dom'
import { useGetTeamDetails } from '../hooks/useGetTeamDetails'
import useAdminAccess from '../../../services/hooks/useAdminAccess'
import { getUserRole } from '../../../services/helpers/getUserRole.'
import { groupRoles } from '../../../constants/roles'
import { useFetchAccount } from '../../Organization/hooks'
import OrganizationsPopup from '../components/OrganizationsPopup'
import usePopup from '../hooks/usePopup'
import successToast from '../../../Components/Toasts/SuccessToast'
import { useQueryClient } from '@tanstack/react-query'
import { useGetOrgGroupUsers } from '../hooks/useGetOrgGroupUsers'
import { useGetOrgDetails } from '../hooks/useGetOrgDetails'
import AddMembersTeamPopupForm from '../components/AddMembersTeamPopupForm'
import TeamUsersTable from '../components/TeamUsersTable'
import { useChangeOrganizationUsersRole } from '../hooks/useChangeOrganizationUsersRole'
import { renderBreadcrumbs } from './breadcrumbs'
import useAccessLevel from '../hooks/useAccessLevel'
import { renderBreadcrumbsTeam } from './breadcrumbsTeam'
import DebounceSearch from '../../../Components/GeneralComponents/DebounceSearch/DebounceSearch'
import { useTeamPageStore } from '../../../Storage/teamPage/useTeamPageStore'
import { useAPI } from '../../../Contexts/APIContext'
import errorToast from '../../../Components/Toasts/ErrorToast'

export default function TeamDetailsMembers() {
  const { o_f_id, g_f_id } = useParams()
  const [users, setUsers] = useState({
    admin: [],
    viewer: [],
    editor: [],
  })
  const { isInternalAdmin, hasOrgAdminAccess, groupRole } = useAccessLevel({
    o_f_id,
    g_f_id,
  })
  const isTeamAdmin = groupRole && groupRole.includes('Administrator')
  const {
    openPopup: addAdminOpenPopup,
    closePopup: addAdminClosePopup,
    isOpen: addAdminIsOpen,
  } = usePopup()

  const queryClient = useQueryClient()

  const {
    data: userData,
    isError: userDataError,
    isLoading: userDataIsLoading,
    isFetching: userDataIsFetching,
  } = useFetchAccount()

  const currentUser = userData?.user
  const currentUserRole = getUserRole(currentUser) || ''
  const { hasFullAdminAccess } = useAdminAccess(currentUser)

  const {
    data: orgDetails,
    isError: orgDetailsError,
    isLoading: orgDetailsIsLoading,
    isFetching: orgDetailsFetching,
  } = useGetOrgDetails({
    id: o_f_id,
  })

  const {
    data: organizationGroup,
    isError: organizationGroupError,
    isLoading: organizationGroupIsLoading,
    isFetching: isOrganizationGroupFetching,
  } = useGetTeamDetails({
    o_f_id,
    g_f_id,
  })

  const {
    data: orgAdmins,
    isError: orgAdminError,
    isLoading: orgAdminIsLoading,
    isFetching: orgAdminIsFetching,
  } = useGetOrgGroupUsers({
    o_f_id,
    g_f_id,
    role: 'Group Administrator',
  })
  const {
    data: orgEditors,
    isError: orgEditorsError,
    isLoading: orgEditorsIsLoading,
    isFetching: orgEditorsIsFetching,
  } = useGetOrgGroupUsers({
    o_f_id,
    g_f_id,
    role: 'Editor',
  })
  const {
    data: orgViewers,
    isError: orgViewersError,
    isLoading: orgViewersIsLoading,
    isFetching: orgViewersIsFetching,
  } = useGetOrgGroupUsers({
    o_f_id,
    g_f_id,
    role: 'Viewer',
  })

  const { mutateAsync: changeOrganizationUsersRole } =
    useChangeOrganizationUsersRole()

  const { getOrganizationUser } = useAPI()

  const handleDeleteGroupUser = async (data, isChangeRole = false) => {
    const newData = {
      ...data,
      o_f_id,
      g_f_id: data.g_f_id || g_f_id,
      _action: 'delete',
    }
    const u_f_id = data.organization_users[0]
    const user = await getOrganizationUser({ o_f_id: o_f_id, u_f_id: u_f_id })
    const groupsCountBeforeDeletion = user.organization_groups.length
    changeOrganizationUsersRole(newData, {
      onSuccess: () => {
        queryClient.invalidateQueries(['organization', 'currentAuthUser'])
        if (isChangeRole) {
          successToast('Access level was successfully updated')
        } else {
          if (groupsCountBeforeDeletion > 1) {
            successToast(
              `The user was removed from this team but still has access to the organization through other ${orgDetails.name} teams or direct Organization membership. To fully remove them, an Organization Admin or Owner must take further action.`
            )
          } else {
            successToast(
              `The user was removed from this team and no longer has access to any artists, but still belongs to the ${orgDetails.name} organization. To fully remove them, an Organization Admin or Owner must take further action.`
            )
          }
        }
      },
      onError: error => {
        errorToast('Error updating User', error)
      },
    })
  }
  const handleChangeOrganizationUsersRole = data => {
    const { oldRole, ...rest } = data
    const newData = {
      ...rest,
      o_f_id,
      g_f_id: data.g_f_id || g_f_id,
      role:
        rest.role === 'Team Administrator' ? 'Group Administrator' : rest.role,
    }
    changeOrganizationUsersRole(newData, {
      onSuccess: () => {
        handleDeleteGroupUser(
          {
            role:
              oldRole === 'Team Administrator'
                ? 'Group Administrator'
                : oldRole,
            organization_users: newData.organization_users,
            g_f_id: newData.g_f_id,
          },
          true
        )
      },
      onError: error => {
        errorToast('Error updating User', error)
      },
    })
  }

  const [filteredUsers, setFilteredUsers] = useState([])
  const inputValue = useTeamPageStore(state => state.inputValue)
  const setInputValue = useTeamPageStore(state => state.setInputValue)
  const searchTerm = useTeamPageStore(state => state.searchTerm)
  const setSearchTerm = useTeamPageStore(state => state.setSearchTerm)

  useEffect(() => {
    if (!orgAdmins && !orgEditors && !orgViewers) return

    const combineUsers = (org, role) => {
      return (org?.organization_users || []).map(user => ({
        ...user,
        role,
      }))
    }

    const allAdminUsers = combineUsers(orgAdmins, 'Team Administrator')
    const allEditorUsers = combineUsers(orgEditors, 'Editor')
    const allViewerUsers = combineUsers(orgViewers, 'Viewer')

    const term = searchTerm?.toLowerCase() || ''

    const filterUsers = array =>
      array.filter(user => {
        const name = user.name?.toLowerCase() || ''
        const email = user.email?.toLowerCase() || ''
        return name.includes(term) || email.includes(term)
      })

    const filteredAdmins = filterUsers(allAdminUsers)
    const filteredEditors = filterUsers(allEditorUsers)
    const filteredViewers = filterUsers(allViewerUsers)

    const sorted = [...filteredAdmins, ...filteredEditors, ...filteredViewers]

    setFilteredUsers(sorted)
  }, [orgAdmins, orgEditors, orgViewers, searchTerm])

  if (
    userDataIsLoading ||
    orgDetailsIsLoading ||
    organizationGroupIsLoading ||
    orgAdminIsLoading ||
    orgEditorsIsLoading ||
    orgViewersIsLoading
  )
    return (
      <Loader
        isLoading={
          userDataIsLoading ||
          orgDetailsIsLoading ||
          organizationGroupIsLoading ||
          orgAdminIsLoading ||
          orgEditorsIsLoading ||
          orgViewersIsLoading
        }
      />
    )

  if (
    userDataError ||
    orgDetailsError ||
    organizationGroupError ||
    orgAdminError ||
    orgEditorsError ||
    orgViewersError
  ) {
    return (
      <Wrapper className={classes.wrapper}>
        <Header>
          <HeaderInfo>
            <PageTitle title={'Error loading data'} />
          </HeaderInfo>
        </Header>
      </Wrapper>
    )
  }

  return (
    <Wrapper className={classes.wrapper}>
      <OrganizationsPopup
        isOpen={addAdminIsOpen}
        closePopup={addAdminClosePopup}
      >
        <AddMembersTeamPopupForm
          closePopup={addAdminClosePopup}
          group={organizationGroup}
        />
      </OrganizationsPopup>
      <Header>
        {orgDetails && (isInternalAdmin || hasOrgAdminAccess) ? (
          <Navigation
            breadcrumbs={renderBreadcrumbs({
              orgName: orgDetails.name,
              teamName: organizationGroup.name,
              o_f_id,
              g_f_id,
            })}
          />
        ) : (
          <Navigation
            breadcrumbs={renderBreadcrumbsTeam({
              orgName: orgDetails.name,
              teamName: organizationGroup.name,
              o_f_id,
              g_f_id,
            })}
          />
        )}
        <HeaderInfo>
          <div className={classes.header}>
            <PageTitle title={organizationGroup?.name} />
            {orgDetails ? (
              <p className={classes.subtitle}>
                A team within the {orgDetails?.name} organization.
              </p>
            ) : null}
          </div>
          {(isInternalAdmin || hasOrgAdminAccess || isTeamAdmin) && (
            <Actions>
              <ButtonOutline
                color={'pink'}
                size="medium"
                text={'ADD MEMBERS'}
                onClick={addAdminOpenPopup}
                disable={!isInternalAdmin && !hasOrgAdminAccess && !isTeamAdmin}
              />
            </Actions>
          )}
        </HeaderInfo>
      </Header>
      <main className={classes.main}>
        {users ? (
          <div className={classes.section}>
            <div className={classes.section__header}>
              <h2 className={classes.section__title}>Members</h2>
              <div>
                <DebounceSearch
                  inputValue={inputValue}
                  setInputValue={setInputValue}
                  searchValue={searchTerm}
                  setSearchValue={setSearchTerm}
                  placeholder={'Search by Name, Email…'}
                />
              </div>
            </div>
            <div className={classes.users}>
              {
                <TeamUsersTable
                  isCurrentUserAnAdmin={hasFullAdminAccess}
                  currentUserRole={currentUserRole}
                  users={filteredUsers}
                  getUserRole={getUserRole}
                  options={groupRoles}
                  onChangeRole={handleChangeOrganizationUsersRole}
                  onDelete={handleDeleteGroupUser}
                />
              }
            </div>
          </div>
        ) : null}
      </main>
    </Wrapper>
  )
}
